import { IEvent } from "@Interfaces";
import { isTimePassed, QUERY_PARAMS } from "@Utils";
import { isDefined } from "repoV2/utils/common/dataTypes/common";
import { IEventModule } from "../root/IEventModule";
import { isDefaultNotSelectedInOfferingVariations } from "../components/OfferingVariationSelector/OfferingVariationSelector.utils";

type EarlyBirdSource =
    | IEvent.IEventData["metadata"]["early_bird_expiry"]
    | IEvent.IEventData["metadata"]["early_bird_expiry_international"];

const getEarlyBirdSource = ({
    eventData,
    areIntlPaymentsEnabled,
}: {
    eventData: IEvent.IUpsellOffering | IEvent.IEventData;
    areIntlPaymentsEnabled: boolean;
}): EarlyBirdSource => {
    return areIntlPaymentsEnabled
        ? eventData?.metadata?.early_bird_expiry_international
        : eventData?.metadata?.early_bird_expiry;
};

/**
 * Note: There is no need to pass `areIntlPaymentsEnabled` if `earlyBirdSource` is passed.
 * `areIntlPaymentsEnabled` is used to evaluate `earlyBirdSource` when not provided.
 */
export const getIsEarlyBirdActive = ({
    eventData,
    earlyBirdSource: earlyBirdSourceProp,
    areIntlPaymentsEnabled,
    isUpsellListing,
}: {
    eventData: IEvent.IUpsellOffering | IEvent.IEventData;
    earlyBirdSource?: EarlyBirdSource;
    areIntlPaymentsEnabled?: boolean;
    isUpsellListing?: boolean;
}): boolean => {
    const earlyBirdSource =
        earlyBirdSourceProp ||
        getEarlyBirdSource({
            eventData,
            areIntlPaymentsEnabled: !!areIntlPaymentsEnabled,
        });

    const initialPrice = isUpsellListing
        ? eventData.updated_price
        : eventData.price_per_head;

    return !!(
        earlyBirdSource &&
        isDefined(earlyBirdSource.price) &&
        earlyBirdSource.price >= 0 &&
        earlyBirdSource.price < initialPrice &&
        // and the deadline is in the future
        earlyBirdSource.time &&
        !isTimePassed(earlyBirdSource.time)
    );
};

/**
 * returns earlyBirdSource when some conditions are satisfied, otherwise returns null.
 */
export const getEarlyBirdObj = ({
    eventData,
    disablePayment,
    isRenewBooking,
    urlQueryParams,
    areIntlPaymentsEnabled,
}: {
    eventData: IEvent.IEventData;
    disablePayment: boolean;
    isRenewBooking: boolean;
    urlQueryParams: IEventModule.IProps["urlQueryParams"];
    areIntlPaymentsEnabled: boolean;
}): IEvent.IEarlyBird | null => {
    if (
        // Only show LTO for default variation in Offering Variations
        // This is for handling any old LTOs as we dont alow creating any new LTO for offering with variations
        isDefaultNotSelectedInOfferingVariations(eventData)
    ) {
        return null;
    }

    const dynamicLink = urlQueryParams?.[QUERY_PARAMS.DYNAMIC_LINK] as string;

    if (eventData && !disablePayment && !isRenewBooking && !dynamicLink) {
        const earlyBirdSource = getEarlyBirdSource({
            eventData,
            areIntlPaymentsEnabled,
        });

        if (getIsEarlyBirdActive({ eventData, earlyBirdSource })) {
            return { text: "", time: 0, price: 0, ...earlyBirdSource };
        }
    }

    return null;
};
